<div class="mx-auto max-w-(--breakpoint-2xl)">
  <div class="relative rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
    @if (isGeneratingPdf) {
      <div class="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/25 backdrop-blur-sm">
        <div class="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-lg">
          <span class="loading-spinner"></span>
          <div class="flex flex-col">
            <span class="text-sm font-semibold text-gray-900">Generating PDF</span>
            <span class="text-xs text-gray-600">Please wait while we prepare your document…</span>
          </div>
        </div>
      </div>
    }

    <div class="rounded-2xl border border-gray-200 p-5 dark:border-gray-800 lg:p-6">
      <div class="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div class="space-y-4">
          <div>
            <h2 class="text-2xl font-semibold text-gray-900 dark:text-white/90">
              {{ event.name }}
            </h2>
          </div>

          <a
            [routerLink]="['/venues', event.venue?.id, 'edit']"
            class="inline-block transition-opacity hover:opacity-80">
            <div class="flex items-center gap-3">
              <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/20">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 class="text-xl font-bold text-gray-900 transition-colors hover:text-brand-600 dark:text-white dark:hover:text-brand-400">
                  {{ event.venue?.name }}
                </h3>
              </div>
            </div>
          </a>
        </div>

        <div class="flex flex-col items-start gap-3 lg:items-end">
          @if (event.start_date) {
            <p class="text-base font-semibold text-gray-900 dark:text-white">
              {{ event.start_date | date: 'EEEE, dd/MM/yyyy' }}
            </p>
          }
          <div class="flex flex-wrap gap-2">
            <app-badge [text]="event.category" [color]="EventModelUtils.getCategoryColor(event.category)"></app-badge>
            <app-badge [text]="event.status" [color]="EventModelUtils.getStatusColor(event.status)"></app-badge>
            @if (subStatusToggleVisible && event.sub_status) {
              <app-badge
                [text]="EventModelUtils.getSubStatusLabel(event.sub_status)"
                [color]="EventModelUtils.getSubStatusColor(event.sub_status)"
                data-testid="sub-status-badge">
              </app-badge>
            }
          </div>

          @if (subStatusToggleVisible) {
            <!-- TVG-119: Active Opportunity sub-status segmented control. -->
            <div
              class="inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800"
              role="group"
              aria-label="Active Opportunity sub-status"
              data-testid="sub-status-toggle">
              <button
                type="button"
                class="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
                [ngClass]="{
                  'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white': isSubStatusActive('event_created'),
                  'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white': !isSubStatusActive('event_created')
                }"
                [disabled]="subStatusUpdating"
                [attr.aria-pressed]="isSubStatusActive('event_created')"
                data-testid="sub-status-toggle-event-created"
                (click)="setSubStatus('event_created')">
                Event Created
              </button>
              <button
                type="button"
                class="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
                [ngClass]="{
                  'bg-white text-warning-700 shadow-sm dark:bg-gray-700 dark:text-warning-300': isSubStatusActive('in_conversation'),
                  'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white': !isSubStatusActive('in_conversation')
                }"
                [disabled]="subStatusUpdating"
                [attr.aria-pressed]="isSubStatusActive('in_conversation')"
                data-testid="sub-status-toggle-in-conversation"
                (click)="setSubStatus('in_conversation')">
                In Conversation
              </button>
            </div>
          }
        </div>
      </div>

      <div class="mt-6 flex flex-wrap justify-end gap-3">
        @if (markAsLostVisible) {
          <!-- TVG-124: Mark as Lost action. Available from Active Opportunity,
               Viewing Booked, and Pencil. Gated behind the CRM Sales Flow toggle. -->
          <app-button
            (click)="openMarkAsLost()"
            [label]="'Mark as Lost'"
            [style]="'secondary'"
            [disabled]="markAsLostSubmitting"
            data-testid="mark-as-lost-button">
          </app-button>
        }
        <!-- TVG-296: the "Cancel booking" action was removed from the booking
             record. See the note on EventViewComponent — the endpoint remains,
             only this button is gone. -->
        <!-- TVG-52: "Create PDF" opens a section picker; select-all reproduces
             the former full event PDF. The standalone Kitchen button stays so
             kitchen staff can produce the Chef Sheet in one tap. -->
        <app-button
          (click)="openPdfBuilder()"
          [label]="'Create PDF'"
          [style]="'secondary'"
          [icon]="FileDown"
          [disabled]="isGeneratingPdf"
          data-testid="create-pdf-button">
        </app-button>
        <app-button
          (click)="downloadKitchenPdf()"
          [label]="'Kitchen / Chef PDF'"
          [style]="'secondary'"
          [icon]="FileDown"
          [disabled]="isGeneratingPdf">
        </app-button>
        @if (tablePlanPdfEnabled && venueHasTablePlanRoom) {
          <!-- TVG-104: printable Table Plan pack. Toggle-gated; hidden until the
               "table-plan-pdf" feature toggle is enabled (default off on prod). -->
          <app-button
            (click)="downloadTablePlanPdf()"
            [label]="'Table Plan PDF'"
            [style]="'secondary'"
            [icon]="FileDown"
            [disabled]="isGeneratingPdf"
            data-testid="table-plan-pdf-button">
          </app-button>
        }
        @if (canCloneEvent) {
          <!-- TVG-120: Clone Event action. Toggle-gated; hidden when crm-sales-flow is off. -->
          <app-button
            (click)="cloneEvent()"
            [label]="'Clone Event'"
            [style]="'secondary'"
            [icon]="CopyIcon"
            [disabled]="isCloning">
          </app-button>
        }
        @if (canCancelViewing) {
          <!-- TVG-121: Cancel a booked viewing. Visible whenever a viewing date is set. -->
          <app-button
            (click)="cancelViewing()"
            [label]="'Cancel viewing'"
            [style]="'secondary'"
            [icon]="XIcon"
            [disabled]="viewingActionInFlight"
            data-testid="cancel-viewing-button">
          </app-button>
        }
        @if (canMarkNoShow) {
          <!-- TVG-121: No-show. Available after the viewing date passes. -->
          <app-button
            (click)="markNoShow()"
            [label]="'Did not attend'"
            [style]="'secondary'"
            [icon]="UserXIcon"
            [disabled]="viewingActionInFlight"
            data-testid="mark-no-show-button">
          </app-button>
        }
        @if (canMoveToPencil) {
          <!-- TVG-122: Move to Pencil (Provisional). Visible to admin/manager
               or the assigned Sales Manager only. Promote anyway override
               handled inside the click handler. -->
          <app-button
            (click)="moveToPencil()"
            [label]="'Move to Provisional'"
            [style]="'primary'"
            [icon]="CalendarIcon"
            [disabled]="pencilActionInFlight"
            data-testid="move-to-pencil-button">
          </app-button>
        }
        @if (canReleasePencil) {
          <!-- TVG-122: Release the held date back to Active Opportunity. -->
          <app-button
            (click)="releasePencil()"
            [label]="'Release date'"
            [style]="'secondary'"
            [icon]="CalendarOffIcon"
            [disabled]="pencilActionInFlight"
            data-testid="release-pencil-button">
          </app-button>
        }
      </div>
    </div>

    <div class="mt-4 border-b border-gray-200 pb-4 sm:hidden dark:border-gray-800">
      <div class="rounded-2xl border border-gray-200 bg-linear-to-br from-white to-gray-50 p-3 shadow-theme-xs dark:border-gray-800 dark:from-gray-900 dark:to-gray-900/70">
        <div class="mb-3">
          <span class="text-[11px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400">
            Section
          </span>
        </div>

        <div class="grid grid-cols-2 gap-2">
          @for (option of visibleTabOptions; track option.value) {
            <button
              type="button"
              class="flex min-h-11 items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium transition-all duration-200"
              [ngClass]="{
                'border-brand-200 bg-brand-50 text-brand-800 shadow-theme-xs dark:border-brand-800 dark:bg-brand-900/30 dark:text-brand-200': tab === option.value,
                'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-800': tab !== option.value
              }"
              (click)="selectTab(option.value)">
              <span>{{ option.label }}</span>
              @if (option.value === 'rules-conduct') {
                @if (event.rules_conduct_submitted_at) {
                  <span
                    class="inline-flex h-2 w-2 rounded-full bg-success-500"
                    title="Submitted"
                    aria-label="Rules and Conduct submitted"></span>
                } @else {
                  <span
                    class="inline-flex h-2 w-2 rounded-full bg-warning-500"
                    title="Not submitted"
                    aria-label="Rules and Conduct not submitted"></span>
                }
              }
            </button>
          }
        </div>
      </div>
    </div>

    <!-- TVG-187: desktop tab bar is driven by the shared visibleTabOptions list
         (same source as the mobile bar) so order, labels and visibility stay in
         lockstep with the canonical menu. -->
    <div class="mt-8 hidden max-w-full overflow-x-auto border-b border-gray-200 pb-6 sm:block dark:border-gray-800">
      <div class="inline-flex min-w-max items-center gap-x-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-900">
        @for (option of visibleTabOptions; track option.value) {
          <button
            class="inline-flex items-center gap-2 rounded-md px-4 py-2 transition-colors hover:text-gray-900 dark:hover:text-white"
            [ngClass]="{
              'bg-white text-gray-900 dark:bg-gray-800 dark:text-white': tab === option.value,
              'text-gray-500 dark:text-gray-400': tab !== option.value
            }"
            (click)="selectTab(option.value)">
            <span>{{ option.label }}</span>
            @if (option.value === 'rules-conduct') {
              @if (event.rules_conduct_submitted_at) {
                <span
                  class="inline-flex h-2 w-2 rounded-full bg-success-500"
                  title="Submitted"
                  aria-label="Rules and Conduct submitted"></span>
              } @else {
                <span
                  class="inline-flex h-2 w-2 rounded-full bg-warning-500"
                  title="Not submitted"
                  aria-label="Rules and Conduct not submitted"></span>
              }
            }
          </button>
        }
      </div>
    </div>

    <div class="mt-8">
      @if (tab === 'event-details') {
        <app-events-edit [embeddedMode]="true" [embeddedEvent]="event" (eventUpdated)="onEventUpdated($event)"></app-events-edit>

        @if (canViewClientAccess && event.id) {
          <div class="mt-6">
            <app-client-access-panel [eventId]="event.id"></app-client-access-panel>
          </div>
        }
      }

      @if (tab === 'itinerary') {
        <app-itinerary
          [event]="event"
          [reloadToken]="itineraryReloadToken"
          [scrollToTimelineItemId]="pendingTimelineScrollId"
          [scrollToken]="itineraryScrollToken">
        </app-itinerary>
      }

      @if (tab === 'financial-summary') {
        <div class="space-y-8">
          <app-financial-summary
            [event]="event"
            (timelineNavigate)="timelineNavigate($event)"
            (eventDataChanged)="reloadEvent()">
          </app-financial-summary>

          <div class="mt-4 rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            <div class="px-5 py-4 sm:px-6 sm:py-5">
              <h3 class="text-base font-medium text-gray-800 dark:text-white/90">
                Guest Allergies
              </h3>
            </div>
            <div class="space-y-6 border-t border-gray-100 p-5 dark:border-gray-800 sm:p-6">
              @if (allergyEditor) {
                <div class="NgxEditor__Wrapper h-auto min-h-40">
                  <ngx-editor-menu [editor]="allergyEditor" [toolbar]="allergyToolbar"></ngx-editor-menu>
                  <ngx-editor
                    [formControl]="allergyForm.controls.guest_allergies"
                    [editor]="allergyEditor"
                    [placeholder]="'Enter guest allergy information here...'"
                    class="min-h-32"
                    style="min-height: 8rem;"
                  ></ngx-editor>
                  <style>
                    .NgxEditor__Content {
                      min-height: 8rem !important;
                      padding: 1rem !important;
                    }
                  </style>
                </div>
              }
            </div>
          </div>
        </div>
      }

      @if (tab === 'guests') {
        <app-event-guests
          [event]="event"
          [openGuestId]="guestToOpenId"
          (guestModalOpened)="onGuestRecordOpened()">
        </app-event-guests>
      }

      @if (tab === 'table-plan' && tablePlanEnabled && venueHasTablePlanRoom) {
        <app-table-plan [event]="event" (openGuest)="onOpenGuestRecord($event)"></app-table-plan>
      }

      @if (tab === 'notes') {
        <app-event-notes [event]="event"></app-event-notes>
      }

      @if (tab === 'suppliers') {
        <app-event-planner-suppliers
          [event]="event"
          (dirtyStateChange)="onSupplierDirtyStateChange($event)">
        </app-event-planner-suppliers>
      }

      @if (tab === 'accommodation') {
        <app-event-accommodation [event]="event"></app-event-accommodation>
      }

      @if (tab === 'rules-conduct') {
        <app-event-rules-conduct
          [event]="event"
          (eventUpdated)="onRulesConductUpdated($event)">
        </app-event-rules-conduct>
      }

      @if (tab === 'planner') {
        <app-event-planner
          [event]="event"
          (dirtyStateChange)="onPlannerDirtyStateChange($event)">
        </app-event-planner>
      }

      @if (tab === 'reminders') {
        <app-event-reminders [event]="event"></app-event-reminders>
      }
    </div>
  </div>

  <!-- TVG-124: Mark as Lost reason modal -->
  <app-mark-as-lost-modal
    [isOpen]="markAsLostOpen"
    [submitting]="markAsLostSubmitting"
    (confirmed)="submitMarkAsLost($event)"
    (cancelled)="closeMarkAsLost()">
  </app-mark-as-lost-modal>

  <!-- TVG-52: Create PDF section picker. -->
  <app-event-pdf-builder-modal
    [isOpen]="pdfBuilderOpen"
    [submitting]="isGeneratingPdf"
    (confirmed)="generateSectionedPdf($event)"
    (cancelled)="closePdfBuilder()">
  </app-event-pdf-builder-modal>
</div>
